import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, FileCode2, Send, Globe, Store, Loader2, ExternalLink } from "lucide-react";

const copy = async (text, label) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
  catch (e) { toast.error("Copy failed — select the text manually"); }
};

export const SeoMeta = ({ item }) => (
  <div className="grid sm:grid-cols-3 gap-2 text-xs" data-testid="article-seo-meta">
    <div className="rounded-md border border-border bg-secondary/20 p-2.5 sm:col-span-1">
      <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Meta title · {item.meta_title?.length || 0} chars</p>
      <p className="mt-1 font-medium" data-testid="article-meta-title">{item.meta_title}</p>
    </div>
    <div className="rounded-md border border-border bg-secondary/20 p-2.5 sm:col-span-2">
      <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Meta description · {item.meta_description?.length || 0} chars</p>
      <p className="mt-1" data-testid="article-meta-description">{item.meta_description}</p>
    </div>
    <div className="rounded-md border border-border bg-secondary/20 p-2.5 sm:col-span-3 font-mono">
      <span className="text-muted-foreground">/</span><span data-testid="article-slug">{item.article_slug}</span>
    </div>
  </div>
);

export default function ArticlePreview({ item, shopifyConnected, onChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const publish = async (dest) => {
    setBusy(dest);
    try {
      const r = await api.post(`/content/items/${item.id}/publish`, { shopify: dest === "shopify", inapp: dest === "inapp", origin_url: window.location.origin, promote: false });
      onChange?.({ ...item, ...r.data });
      toast.success(dest === "shopify" ? "Published to your Shopify blog" : "Published to your AutoGridSocial blog");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(""); }
  };
  const toPost = async () => {
    setBusy("post");
    try {
      await api.post(`/content/items/${item.id}/to-post`, { platform: "linkedin" });
      toast.success("Social draft created", { description: "It links to the article. Review it in the calendar.", action: { label: "Open calendar", onClick: () => navigate("/calendar") } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setBusy(""); }
  };
  const live = item.shopify_url || item.inapp_url;
  return (
    <div className="space-y-4" data-testid="article-preview">
      <SeoMeta item={item} />
      <Tabs defaultValue="preview">
        <TabsList className="bg-secondary/40">
          <TabsTrigger value="preview" data-testid="article-tab-preview">Preview</TabsTrigger>
          <TabsTrigger value="markdown" data-testid="article-tab-markdown">Markdown</TabsTrigger>
          <TabsTrigger value="html" data-testid="article-tab-html">HTML</TabsTrigger>
        </TabsList>
        <TabsContent value="preview">
          <article className="ags-article max-h-[48vh] overflow-y-auto rounded-lg border border-border bg-background p-5"
            data-testid="article-rendered" dangerouslySetInnerHTML={{ __html: item.rendered_html }} />
        </TabsContent>
        <TabsContent value="markdown">
          <pre className="max-h-[48vh] overflow-auto rounded-lg border border-border bg-background p-4 text-xs whitespace-pre-wrap font-mono" data-testid="article-markdown">{item.rendered_content}</pre>
        </TabsContent>
        <TabsContent value="html">
          <pre className="max-h-[48vh] overflow-auto rounded-lg border border-border bg-background p-4 text-xs whitespace-pre-wrap font-mono" data-testid="article-html">{item.rendered_html}</pre>
        </TabsContent>
      </Tabs>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-border" onClick={() => copy(item.rendered_content, "Markdown")} data-testid="copy-markdown-btn"><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Raw Markdown</Button>
        <Button size="sm" variant="outline" className="border-border" onClick={() => copy(item.rendered_html, "HTML")} data-testid="copy-html-btn"><FileCode2 className="h-3.5 w-3.5 mr-1.5" /> Copy HTML</Button>
        <Button size="sm" variant="outline" className="border-border" disabled={!!busy} onClick={toPost} data-testid="export-to-post-btn">
          {busy === "post" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />} Export to Post Builder
        </Button>
        <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" disabled={!!busy || !!item.inapp_url} onClick={() => publish("inapp")} data-testid="publish-inapp-btn">
          {busy === "inapp" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />} {item.inapp_url ? "On your blog" : "Publish to blog page"}
        </Button>
        {shopifyConnected && (
          <Button size="sm" className="bg-[#5E8E3E] hover:bg-[#4f7a33] text-white" disabled={!!busy || !!item.shopify_url} onClick={() => publish("shopify")} data-testid="publish-shopify-btn">
            {busy === "shopify" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Store className="h-3.5 w-3.5 mr-1.5" />} {item.shopify_url ? "On Shopify" : "Publish to Shopify"}
          </Button>
        )}
      </div>
      {live && (
        <a href={live} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="article-live-link">
          <ExternalLink className="h-3.5 w-3.5" /> {live}
        </a>
      )}
    </div>
  );
}
